import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconArrowLeft, IconLoader2, IconRefresh } from '@tabler/icons-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import api from '@/lib/api';
import PageHeader from '@/components/PageHeader';

interface IotCustomer {
  id: number;
  i_customer: number;
  company_name: string | null;
  code: string | null;
  company_registration_number: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  suburb: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  id_number: string | null;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  xero_contact_id: string | null;
}

interface IotAccount {
  id: number;
  i_customer: number;
  package_id: number | null;
  i_account: number;
  msisdn: string;
  alias: string | null;
  voice_balance: number;
  sms_balance: number;
  data_balance: number;
  ip: string | null;
  created_at: string;
  updated_at: string;
  package_name?: string | null;
  data_value?: number | null;
  voice_value?: number | null;
  sms_value?: number | null;
  data_reload?: number | null;
  voice_reload?: number | null;
  sms_reload?: number | null;
  price?: number | null;
}

export function IotCustomerAccountsPage() {
  const { iCustomer } = useParams<{ iCustomer: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<IotCustomer | null>(null);
  const [accounts, setAccounts] = useState<IotAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (iCustomer) {
      loadCustomerData(parseInt(iCustomer, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadCustomerData is not wrapped in useCallback; runs when iCustomer changes
  }, [iCustomer]);

  const loadCustomerData = async (iCustomerNum: number) => {
    setLoading(true);
    setError('');
    try {
      // Try to get customer by i_customer (we'll need to add this endpoint or use search)
      const searchResponse = await api.get(`/iot/search-customer?query=${iCustomerNum}`);
      if (searchResponse.data.customers && searchResponse.data.customers.length > 0) {
        setCustomer(searchResponse.data.customers[0]);
        loadAccounts(iCustomerNum);
      } else {
        setError('Customer not found');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async (iCustomerNum: number) => {
    setLoadingAccounts(true);
    try {
      const response = await api.get(`/iot/customer/${iCustomerNum}/accounts`);
      setAccounts(response.data.accounts || []);
    } catch (err: any) {
      console.error('Failed to load accounts:', err);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return `R ${value.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <IconLoader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div>
        <Button type="button" className="mb-2 gap-1.5" onClick={() => navigate('/iot')}>
          <IconArrowLeft className="size-4" aria-hidden />
          Back to IoT
        </Button>
        <Alert variant="destructive">
          <AlertDescription>{error || 'Customer not found'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <Button type="button" className="mb-2 gap-1.5" onClick={() => navigate('/iot')}>
        <IconArrowLeft className="size-4" aria-hidden />
        Back to Customers
      </Button>

      <PageHeader title="Customer Accounts" />

      {/* Customer Details */}
      <Card className="mb-6">
        <CardContent>
          <h2 className="font-heading text-base font-semibold leading-snug">Customer Information</h2>
          <Separator className="my-4" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs text-muted-foreground">i_customer</p>
              <p className="text-base font-bold">{customer.i_customer}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Company Name</p>
              <p className="text-base">{customer.company_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">First Name</p>
              <p className="text-base">{customer.first_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Name</p>
              <p className="text-base">{customer.last_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="text-base">{customer.email_address || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">ID Number</p>
              <p className="text-base">{customer.id_number || 'N/A'}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-muted-foreground">Address</p>
              <p className="text-base">
                {[
                  customer.address_line_1,
                  customer.address_line_2,
                  customer.suburb,
                  customer.city,
                  customer.province,
                  customer.postal_code,
                ]
                  .filter(Boolean)
                  .join(', ') || 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <Card>
        <CardContent>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-base font-semibold leading-snug">
              Accounts ({accounts.length})
            </h2>
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              onClick={() => customer && loadAccounts(customer.i_customer)}
              disabled={loadingAccounts}
            >
              <IconRefresh className="size-3.5" aria-hidden />
              Refresh
            </Button>
          </div>
          {loadingAccounts ? (
            <div className="flex justify-center p-6">
              <IconLoader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
              <span className="sr-only">Loading accounts</span>
            </div>
          ) : accounts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>MSISDN</TableHead>
                  <TableHead>Alias</TableHead>
                  <TableHead>i_account</TableHead>
                  <TableHead>Package</TableHead>
                  <TableHead className="text-right">Data Balance</TableHead>
                  <TableHead className="text-right">Voice Balance</TableHead>
                  <TableHead className="text-right">SMS Balance</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.msisdn}</TableCell>
                    <TableCell>{account.alias || 'N/A'}</TableCell>
                    <TableCell>{account.i_account}</TableCell>
                    <TableCell>{account.package_name || 'N/A'}</TableCell>
                    <TableCell className="text-right">{formatCurrency(account.data_balance)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(account.voice_balance)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(account.sms_balance)}</TableCell>
                    <TableCell>{account.ip || 'N/A'}</TableCell>
                    <TableCell>{formatDate(account.created_at)}</TableCell>
                    <TableCell>{formatDate(account.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Alert className="border-blue-200 bg-blue-50 text-blue-800">
              <AlertDescription>No accounts found for this customer</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
